import React, { useState } from 'react';
import { Copy, Check, X, Mail, QrCode, Shield, ShieldCheck, CheckCircle, CalendarClock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { QRCodeSVG } from 'qrcode.react';

const ShareLinkDisplay = ({ shareData, onClose, onSendEmail, isPro = false }) => {
  const [copied, setCopied] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const { toast } = useToast();

  const isEmailMode = shareData.email_sent === true;
  const isScheduledMode = shareData.email_scheduled === true;

  const baseShareUrl = `${window.location.origin}/share/${shareData.share_id}`;
  const shareUrl = shareData.encryption_key 
    ? `${baseShareUrl}#key=${shareData.encryption_key}` 
    : baseShareUrl;
  const isEncrypted = !!shareData.encryption_key || shareData.is_encrypted;

  const expiryDays = shareData.expiry_days || (isPro ? 30 : 7);
  const maxDownloads = shareData.max_downloads != null ? shareData.max_downloads : (isPro ? 0 : 10);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast({ title: 'Lien copié !', description: 'Le lien de partage a été copié dans le presse-papiers' });
    setTimeout(() => setCopied(false), 2000);
  };

  // Email confirmation view
  if (isEmailMode || isScheduledMode) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4" data-testid="share-link-modal">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-600/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative bg-slate-900/95 border border-blue-500/30 rounded-3xl shadow-2xl max-w-lg w-full animate-in zoom-in-95 overflow-hidden">
          <button onClick={onClose} className="absolute top-4 right-4 text-blue-300/60 hover:text-white transition-colors z-10" data-testid="close-share-modal-btn">
            <X className="w-6 h-6" />
          </button>

          <div className="p-8 text-center">
            {/* Success icon */}
            <div className="flex justify-center mb-6">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center shadow-2xl ${isScheduledMode ? 'bg-gradient-to-br from-amber-500 to-orange-500 shadow-amber-500/30' : 'bg-gradient-to-br from-emerald-500 to-green-500 shadow-emerald-500/30'}`}>
                {isScheduledMode 
                  ? <CalendarClock className="w-10 h-10 text-white" />
                  : <Mail className="w-10 h-10 text-white" />
                }
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold mb-4 italic">
              <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
                {isScheduledMode ? 'Envoi programmé !' : 'Email envoyé !'}
              </span>
            </h1>

            <p className="text-blue-200/80 text-base mb-2">
              {isScheduledMode 
                ? 'Votre fichier sera envoyé à la date programmée à'
                : 'Un lien de téléchargement a été envoyé à'
              }
            </p>
            <p className="text-cyan-400 font-semibold text-lg mb-6" data-testid="recipient-email-display">
              {shareData.recipient_email}
            </p>

            {/* File info */}
            <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-200/70">
                {shareData.file_count > 1 
                  ? `${shareData.file_count} fichiers envoyés` 
                  : `Fichier : ${shareData.filename}`
                }
              </p>
            </div>

            {/* Expiration info */}
            <div className="flex items-center justify-center gap-2 text-sm text-blue-200/70 mb-6 flex-wrap">
              <span>Disponible pendant</span>
              <span className="font-medium text-white">{expiryDays} jour{expiryDays > 1 ? 's' : ''}</span>
              <span className="mx-1">—</span>
              <span className="font-medium text-white">
                {maxDownloads === 0 ? 'Téléchargements illimités' : `${maxDownloads} téléchargements max`}
              </span>
            </div>

            {/* Status badges */}
            <div className="flex items-center justify-center gap-6 pt-6 border-t border-blue-500/20">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg ${isEncrypted ? 'bg-green-500 shadow-green-500/30' : 'bg-slate-600 shadow-slate-500/20'}`}>
                  {isEncrypted ? <ShieldCheck className="w-7 h-7 text-white" /> : <Shield className="w-7 h-7 text-white" />}
                </div>
                <div className="text-left">
                  <p className={isEncrypted ? 'text-green-400 font-semibold' : 'text-slate-400 font-semibold'}>
                    {isEncrypted ? 'Chiffré E2E' : 'Non chiffré'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg ${isScheduledMode ? 'bg-amber-500 shadow-amber-500/30' : 'bg-emerald-500 shadow-emerald-500/30'}`}>
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
                <div className="text-left">
                  <p className={isScheduledMode ? 'text-amber-400 font-semibold' : 'text-emerald-400 font-semibold'}>
                    {isScheduledMode ? 'Programmé' : 'Envoyé'}
                  </p>
                </div>
              </div>
            </div>

            {/* Close button */}
            <Button onClick={onClose}
              className="mt-8 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold px-8 py-3 rounded-xl shadow-lg shadow-blue-500/30"
              data-testid="close-email-confirmation-btn">
              Fermer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Link sharing view (default)
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4" data-testid="share-link-modal">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-600/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative bg-slate-900/95 border border-blue-500/30 rounded-3xl shadow-2xl max-w-lg w-full animate-in zoom-in-95 overflow-hidden">
        <button onClick={onClose} className="absolute top-4 right-4 text-blue-300/60 hover:text-white transition-colors z-10" data-testid="close-share-modal-btn">
          <X className="w-6 h-6" />
        </button>

        <div className="p-8">
          <h1 className="text-3xl md:text-4xl font-bold text-center mb-8 italic">
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Ton fichier est prêt à être envoyé !
            </span>
          </h1>

          <div className="space-y-4">
            <p className="text-center text-blue-200/80 text-sm">Copie le lien pour partager ton fichier</p>

            <div className="flex items-center gap-2">
              <input type="text" readOnly value={shareUrl}
                className="flex-1 bg-slate-800/50 border border-blue-500/30 rounded-xl px-4 py-3.5 text-white text-sm font-mono truncate focus:outline-none"
                data-testid="share-url-input" />
              <Button onClick={copyToClipboard}
                className="bg-blue-200 hover:bg-white text-slate-900 font-semibold px-5 py-3.5 rounded-xl transition-all flex items-center gap-2"
                data-testid="copy-link-btn">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span className="hidden sm:inline">Copier le lien</span>
              </Button>
            </div>

            <div className="flex justify-center mt-4">
              <Button variant="outline" onClick={() => setShowQRCode(!showQRCode)}
                className={`border-blue-500/30 text-white py-3.5 px-6 rounded-xl flex items-center justify-center gap-2 transition-all ${showQRCode ? 'bg-blue-600/30 border-blue-400' : 'bg-slate-800/50 hover:bg-slate-700/50'}`}
                data-testid="show-qr-btn">
                <QrCode className="w-4 h-4" /> Voir le QR Code
              </Button>
            </div>

            {showQRCode && (
              <div className="flex justify-center py-4 animate-in fade-in slide-in-from-top-2">
                <div className="bg-white p-4 rounded-xl">
                  <QRCodeSVG value={shareUrl} size={160} level="H" includeMargin={false} />
                </div>
              </div>
            )}

            {/* Expiration & download limits from plan */}
            <div className="flex items-center justify-center gap-2 text-sm text-blue-200/70 mt-4 flex-wrap">
              <span>Suppression dans</span>
              <span className="font-medium text-white">{expiryDays} jour{expiryDays > 1 ? 's' : ''}</span>
              <span className="mx-1">—</span>
              <span className="font-medium text-white">
                {maxDownloads === 0 ? 'Téléchargements illimités' : `${maxDownloads} téléchargements max`}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-center gap-8 mt-8 pt-6 border-t border-blue-500/20">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg ${isEncrypted ? 'bg-green-500 shadow-green-500/30' : 'bg-slate-600 shadow-slate-500/20'}`}>
                {isEncrypted ? <ShieldCheck className="w-7 h-7 text-white" /> : <Shield className="w-7 h-7 text-white" />}
              </div>
              <div>
                <p className={isEncrypted ? 'text-green-400 font-semibold' : 'text-slate-400 font-semibold'}>
                  {isEncrypted ? 'Chiffré E2E' : 'Non chiffré'}
                </p>
                <p className={`text-xs ${isEncrypted ? 'text-green-400/70' : 'text-slate-400/50'}`}>
                  {isEncrypted ? 'Clé incluse dans le lien' : 'Transfert standard'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-blue-400 font-semibold">Envoyé</p>
                <p className="text-blue-400/70 text-xs">Tu peux fermer cette page maintenant</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareLinkDisplay;
